import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { 
  Instagram, Facebook, Linkedin, Youtube, Twitter,
  Calendar, Clock, Sparkles, Copy, Send, Image as ImageIcon,
  ChevronLeft, ChevronRight, Plus, Settings, Loader2,
  Check, AlertCircle, Edit3, Trash2, MoreVertical, X,
  Upload, FileImage, Type
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const platforms = [
  { id: 'all', label: 'All Platforms', icon: null },
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'text-pink-500', bg: 'bg-gradient-to-r from-purple-500 to-pink-500' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'text-blue-500', bg: 'bg-blue-600' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'text-blue-600', bg: 'bg-blue-700' },
  { id: 'twitter', label: 'X (Twitter)', icon: Twitter, color: 'text-zinc-400', bg: 'bg-zinc-800' },
  { id: 'tiktok', label: 'TikTok', icon: null, color: 'text-white', bg: 'bg-black' },
  { id: 'youtube', label: 'YouTube', icon: Youtube, color: 'text-red-500', bg: 'bg-red-600' },
];

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const hashtagSuggestions = [
  '#SummerSale', '#FashionStyle', '#TrendingNow', '#NewArrivals', 
  '#StyleInspo', '#OOTD', '#ShopNow', '#LimitedEdition'
];

export default function SocialMedia() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [activePlatform, setActivePlatform] = useState('all');
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [caption, setCaption] = useState('');
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  
  // Create Post Modal State
  const [postContent, setPostContent] = useState('');
  const [postImage, setPostImage] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['facebook']);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [creatingPost, setCreatingPost] = useState(false);
  const [publishingNow, setPublishingNow] = useState(false);
  const [savedAssets, setSavedAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [activeTab, setActiveTab] = useState('compose'); // compose, library

  // Check for createPost param and sessionStorage on load
  useEffect(() => {
    if (searchParams.get('createPost') === 'true') {
      setShowCreatePost(true);
      
      // Load content from Content Studio if available
      const storedContent = sessionStorage.getItem('postContent');
      if (storedContent) {
        try {
          const parsed = JSON.parse(storedContent);
          if (parsed.content) setPostContent(parsed.content);
          if (parsed.image_base64) setPostImage(parsed.image_base64);
          sessionStorage.removeItem('postContent');
        } catch (e) {
          console.error('Failed to parse stored content');
        }
      }
    }
  }, [searchParams]);

  // Fetch scheduled posts
  useEffect(() => {
    fetchScheduledPosts();
  }, [token]);

  const fetchScheduledPosts = async () => {
    try {
      const response = await fetch(`${API}/api/marketing/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Transform posts to calendar format
        const posts = (data.posts || []).map(post => ({
          id: post.id,
          platform: post.platforms?.[0] || 'facebook',
          time: post.scheduled_time || '09:00',
          date: post.scheduled_date,
          status: post.status,
          title: post.content?.substring(0, 30) + (post.content?.length > 30 ? '...' : '') || 'Untitled'
        }));
        setScheduledPosts(posts);
      }
    } catch (error) {
      console.error('Failed to fetch scheduled posts:', error);
    }
  };

  // Fetch saved assets from Content Studio
  useEffect(() => {
    if (showCreatePost) {
      fetchSavedAssets();
    }
  }, [showCreatePost]);

  const fetchSavedAssets = async () => {
    setLoadingAssets(true);
    try {
      const response = await fetch(`${API}/api/marketing/content-library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedAssets(data.assets || []);
      }
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleCreatePost = async () => {
    if (!postContent.trim() && !postImage) {
      toast.error('Please add content or select an image');
      return;
    }
    
    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    setCreatingPost(true);
    try {
      const response = await fetch(`${API}/api/marketing/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          content: postContent,
          image: postImage,
          platforms: selectedPlatforms,
          scheduled_date: scheduleDate,
          scheduled_time: scheduleTime,
          status: scheduleDate ? 'scheduled' : 'draft'
        })
      });

      if (response.ok) {
        toast.success(scheduleDate ? 'Post scheduled successfully!' : 'Post saved as draft!');
        setShowCreatePost(false);
        resetPostForm();
        fetchScheduledPosts(); // Refresh calendar
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create post');
      }
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setCreatingPost(false);
    }
  };

  const handlePublishNow = async () => {
    if (!postContent && !postImage) {
      toast.error('Please add content or an image');
      return;
    }

    if (!selectedPlatforms.includes('facebook')) {
      toast.error('Please select Facebook to publish');
      return;
    }

    setPublishingNow(true);
    try {
      const response = await fetch(`${API}/api/marketing/publish-now`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          content: postContent,
          image: postImage
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Posted to Facebook successfully!');
        if (data.fb_post_url) {
          toast.info(
            <div>
              <p>View your post:</p>
              <a 
                href={data.fb_post_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Open on Facebook
              </a>
            </div>,
            { duration: 10000 }
          );
        }
        setShowCreatePost(false);
        resetPostForm();
      } else {
        toast.error(data.detail || 'Failed to publish to Facebook');
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Failed to publish to Facebook');
    } finally {
      setPublishingNow(false);
    }
  };

  const resetPostForm = () => {
    setPostContent('');
    setPostImage(null);
    setSelectedPlatforms(['instagram']);
    setScheduleDate('');
    setScheduleTime('');
    setActiveTab('compose');
  };

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const selectAsset = (asset) => {
    if (asset.type === 'image' && asset.image_base64) {
      setPostImage(asset.image_base64);
    }
    if (asset.content) {
      setPostContent(asset.content);
    }
    setActiveTab('compose');
    toast.success('Asset added to post');
  };

  // Generate week dates
  const getWeekDates = () => {
    const start = new Date(currentWeek);
    start.setDate(start.getDate() - start.getDay());
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  const generateCaption = async () => {
    if (!caption.trim()) {
      toast.error('Please enter a prompt for caption generation');
      return;
    }

    setGeneratingCaption(true);
    try {
      const response = await fetch(`${API}/api/marketing/generate-caption`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ prompt: caption, platform: activePlatform })
      });

      if (response.ok) {
        const data = await response.json();
        setCaption(data.caption || 'AI generated caption will appear here...');
        toast.success('Caption generated successfully!');
      } else {
        // Demo caption for now
        setCaption(`✨ Summer vibes are calling! 🌴\n\nDiscover our latest collection with amazing deals up to 30% off! Don't miss out on the hottest trends of the season.\n\n🔥 Limited time only!\n📍 Shop now - link in bio\n\n#SummerSale #Fashion #NewCollection #TrendAlert`);
        toast.success('Caption generated!');
      }
    } catch (error) {
      // Demo caption fallback
      setCaption(`✨ Summer vibes are calling! 🌴\n\nDiscover our latest collection with amazing deals up to 30% off! Don't miss out on the hottest trends of the season.\n\n🔥 Limited time only!\n📍 Shop now - link in bio\n\n#SummerSale #Fashion #NewCollection #TrendAlert`);
      toast.success('Caption generated!');
    } finally {
      setGeneratingCaption(false);
    }
  };

  const copyHashtags = () => {
    navigator.clipboard.writeText(hashtagSuggestions.join(' '));
    toast.success('Hashtags copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      published: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      draft: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      ai_generated: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    };
    const labels = {
      scheduled: 'Scheduled',
      published: 'Published',
      draft: 'Draft',
      ai_generated: 'AI Generated',
    };
    return (
      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getPlatformIcon = (platformId) => {
    const platform = platforms.find(p => p.id === platformId);
    if (platform?.icon) {
      const Icon = platform.icon;
      return <Icon className={`w-4 h-4 ${platform.color}`} />;
    }
    if (platformId === 'tiktok') {
      return <span className="text-xs font-bold">TT</span>;
    }
    return null;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Social Media Automation Hub</h1>
          <p className="text-muted-foreground">Manage all your social media platforms in one place</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowCreatePost(true)}
            className="marketing-btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Post</span>
          </button>
        </div>
      </div>

      {/* Platform Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {platforms.map((platform) => (
          <button
            key={platform.id}
            onClick={() => setActivePlatform(platform.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
              transition-all duration-200
              ${activePlatform === platform.id 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-secondary hover:bg-secondary/80 text-foreground'}
            `}
          >
            {platform.icon && <platform.icon className="w-4 h-4" />}
            {platform.id === 'tiktok' && <span className="font-bold">TT</span>}
            {platform.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <div className="lg:col-span-2 marketing-card">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold">Content Calendar</h3>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => {
                  const newDate = new Date(currentWeek);
                  newDate.setDate(newDate.getDate() - 7);
                  setCurrentWeek(newDate);
                }}
                className="p-2 hover:bg-secondary rounded-lg"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium px-4">
                {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              <button 
                onClick={() => {
                  const newDate = new Date(currentWeek);
                  newDate.setDate(newDate.getDate() + 7);
                  setCurrentWeek(newDate);
                }}
                className="p-2 hover:bg-secondary rounded-lg"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Day Headers */}
            {weekDates.map((date, index) => (
              <div key={index} className="text-center pb-2 border-b border-border">
                <p className="text-xs text-muted-foreground">{weekDays[date.getDay()]}</p>
                <p className={`text-sm font-medium mt-1 ${
                  date.toDateString() === new Date().toDateString() ? 'text-primary' : ''
                }`}>
                  {date.getDate()}
                </p>
              </div>
            ))}

            {/* Time Slots */}
            {weekDates.map((date, dayIndex) => {
              const dateStr = date.toISOString().split('T')[0];
              const dayPosts = scheduledPosts.filter(post => {
                const postDateMatch = post.date === dateStr;
                const platformMatch = activePlatform === 'all' || post.platform === activePlatform;
                return postDateMatch && platformMatch;
              });
              
              return (
                <div key={dayIndex} className="space-y-1 min-h-[200px] pt-2">
                  {dayPosts.slice(0, 3).map((post, postIndex) => (
                    <div 
                      key={postIndex}
                      className="p-2 bg-secondary/50 rounded-lg border border-border hover:border-primary/30 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-1 mb-1">
                        {getPlatformIcon(post.platform)}
                        <span className="text-[10px] text-muted-foreground">{post.time}</span>
                      </div>
                      <p className="text-xs truncate">{post.title}</p>
                      {getStatusBadge(post.status)}
                    </div>
                  ))}
                  {dayPosts.length > 3 && (
                    <p className="text-xs text-muted-foreground text-center">+{dayPosts.length - 3} more</p>
                  )}
                  <button 
                    onClick={() => {
                      setSelectedDate(date);
                      setScheduleDate(dateStr);
                      setShowCreatePost(true);
                    }}
                    className="w-full p-2 border border-dashed border-border rounded-lg text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                  >
                    <Plus className="w-4 h-4 mx-auto" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border text-xs">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-400"></div>
              <span className="text-muted-foreground">Scheduled</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
              <span className="text-muted-foreground">Published</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-400"></div>
              <span className="text-muted-foreground">Draft</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-400"></div>
              <span className="text-muted-foreground">AI Generated</span>
            </div>
          </div>
        </div>

        {/* Right Panel - AI Assistant */}
        <div className="space-y-4">
          {/* Caption Generator */}
          <div className="marketing-card">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">AI Caption Generator</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">What's your post about?</label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="E.g., Create an engaging caption for a summer sale post for fashion brand..."
                  className="marketing-input h-24 resize-none"
                />
              </div>

              <button
                onClick={generateCaption}
                disabled={generatingCaption}
                className="marketing-btn-primary w-full flex items-center justify-center gap-2"
              >
                {generatingCaption ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate Caption
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Hashtag Suggestions */}
          <div className="marketing-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Hashtag Suggestions</h3>
              <button onClick={copyHashtags} className="text-primary text-xs hover:underline flex items-center gap-1">
                <Copy className="w-3 h-3" /> Copy All
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {hashtagSuggestions.map((tag, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 bg-secondary rounded-lg text-xs text-primary cursor-pointer hover:bg-primary/20 transition-colors"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Best Time to Post */}
          <div className="marketing-card">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-sm">Best Time to Post</h3>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
              <p className="text-lg font-bold text-primary">7:00 PM - 9:00 PM</p>
              <p className="text-xs text-muted-foreground mt-1">Based on your audience activity</p>
            </div>
          </div>

          {/* Engagement Prediction */}
          <div className="marketing-card">
            <h3 className="font-semibold text-sm mb-4">Engagement Prediction</h3>
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    stroke="hsl(30 10% 20%)"
                    strokeWidth="4"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    stroke="hsl(38 75% 55%)"
                    strokeWidth="4"
                    strokeDasharray={`${87 * 1.76} 176`}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">87%</span>
              </div>
              <div>
                <p className="font-medium text-emerald-400">High Engagement</p>
                <p className="text-xs text-muted-foreground">Expected to perform well</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Post Modal */}
      {showCreatePost && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-border">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold">Create New Post</h2>
              <button 
                onClick={() => { setShowCreatePost(false); resetPostForm(); }}
                className="p-2 hover:bg-secondary rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border">
              <button
                onClick={() => setActiveTab('compose')}
                className={`flex-1 px-4 py-3 text-sm font-medium ${activeTab === 'compose' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
              >
                <Edit3 className="w-4 h-4 inline mr-2" />
                Compose
              </button>
              <button
                onClick={() => setActiveTab('library')}
                className={`flex-1 px-4 py-3 text-sm font-medium ${activeTab === 'library' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
              >
                <FileImage className="w-4 h-4 inline mr-2" />
                Content Library
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {activeTab === 'compose' ? (
                <div className="space-y-4">
                  {/* Platform Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Select Platforms</label>
                    <div className="flex flex-wrap gap-2">
                      {platforms.filter(p => p.id !== 'all').map((platform) => (
                        <button
                          key={platform.id}
                          onClick={() => togglePlatform(platform.id)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all ${
                            selectedPlatforms.includes(platform.id)
                              ? 'bg-primary/20 border-primary text-primary'
                              : 'bg-secondary border-border hover:border-primary/50'
                          }`}
                        >
                          {platform.icon && <platform.icon className="w-4 h-4" />}
                          {platform.id === 'tiktok' && <span className="font-bold text-xs">TT</span>}
                          {platform.label}
                          {selectedPlatforms.includes(platform.id) && <Check className="w-3 h-3" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Image Preview */}
                  {postImage && (
                    <div className="relative">
                      <label className="block text-sm font-medium mb-2">Selected Image</label>
                      <div className="relative rounded-lg overflow-hidden border border-border">
                        <img 
                          src={`data:image/png;base64,${postImage}`} 
                          alt="Post" 
                          className="w-full max-h-48 object-cover"
                        />
                        <button
                          onClick={() => setPostImage(null)}
                          className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Caption/Content */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Caption</label>
                    <textarea
                      value={postContent}
                      onChange={(e) => setPostContent(e.target.value)}
                      placeholder="Write your caption here... Use emojis and hashtags!"
                      className="marketing-input h-32 resize-none"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{postContent.length}/2200 characters</p>
                  </div>

                  {/* AI Caption Generator */}
                  <div className="p-3 bg-secondary/50 rounded-lg border border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">AI Caption Helper</span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Describe your post for AI caption..."
                        className="marketing-input flex-1 h-9 text-sm"
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && e.target.value) {
                            setGeneratingCaption(true);
                            try {
                              const response = await fetch(`${API}/api/marketing/generate-caption`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`
                                },
                                body: JSON.stringify({ prompt: e.target.value, platform: selectedPlatforms[0] || 'instagram' })
                              });
                              if (response.ok) {
                                const data = await response.json();
                                setPostContent(data.caption);
                                toast.success('Caption generated!');
                              }
                            } catch (error) {
                              toast.error('Failed to generate caption');
                            } finally {
                              setGeneratingCaption(false);
                            }
                          }
                        }}
                      />
                      {generatingCaption && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Press Enter to generate</p>
                  </div>

                  {/* Schedule */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Schedule Date (Optional)</label>
                      <input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        className="marketing-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Schedule Time</label>
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="marketing-input"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* Content Library Tab */
                <div>
                  {loadingAssets ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : savedAssets.length === 0 ? (
                    <div className="text-center py-12">
                      <FileImage className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No saved content yet</p>
                      <p className="text-sm text-muted-foreground mt-1">Generate content in Content Studio first</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {savedAssets.map((asset, index) => (
                        <button
                          key={index}
                          onClick={() => selectAsset(asset)}
                          className="p-2 bg-secondary/50 rounded-lg border border-border hover:border-primary transition-colors text-left"
                        >
                          {asset.image_base64 ? (
                            <img 
                              src={`data:image/png;base64,${asset.image_base64}`}
                              alt={asset.title}
                              className="w-full h-24 object-cover rounded mb-2"
                            />
                          ) : (
                            <div className="w-full h-24 bg-secondary rounded mb-2 flex items-center justify-center">
                              <Type className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                          <p className="text-xs font-medium truncate">{asset.title || 'Untitled'}</p>
                          <p className="text-[10px] text-muted-foreground">{asset.type}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-4 border-t border-border bg-secondary/30">
              <button
                onClick={() => { setShowCreatePost(false); resetPostForm(); }}
                className="marketing-btn-secondary"
              >
                Cancel
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleCreatePost()}
                  disabled={creatingPost || publishingNow}
                  className="marketing-btn-secondary flex items-center gap-2"
                >
                  Save as Draft
                </button>
                <button
                  onClick={handlePublishNow}
                  disabled={publishingNow || creatingPost || !selectedPlatforms.includes('facebook')}
                  className="marketing-btn-primary flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                  data-testid="publish-now-btn"
                >
                  {publishingNow ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Facebook className="w-4 h-4" />
                  )}
                  {publishingNow ? 'Publishing...' : 'Publish to Facebook Now'}
                </button>
                <button
                  onClick={() => handleCreatePost()}
                  disabled={creatingPost || publishingNow || !scheduleDate}
                  className="marketing-btn-primary flex items-center gap-2"
                >
                  {creatingPost ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {scheduleDate ? 'Schedule Post' : 'Select Date'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
